import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ShoppingCart, TrendingUp } from "lucide-react";

interface GameNFT {
  id: string;
  name: string;
  image_url: string | null;
  current_price: number;
  owner_display_name?: string;
}

interface GamePurchaseModalProps {
  nft: GameNFT | null;
  playerId: string;
  balance: number;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function GamePurchaseModal({ nft, playerId, balance, open, onClose, onSuccess }: GamePurchaseModalProps) {
  const [purchasing, setPurchasing] = useState(false);

  if (!nft) return null;

  const canAfford = balance >= nft.current_price;
  const nextPrice = Math.round((nft.current_price * 1.25 + 0.01) * 100) / 100;

  const handlePurchase = async () => {
    setPurchasing(true);
    try {
      const { data, error } = await supabase.rpc("game_purchase_nft", {
        p_nft_id: nft.id,
        p_buyer_player_id: playerId,
        p_expected_price: nft.current_price,
      });

      if (error) throw error;

      const result = data as any;
      if (result?.success) {
        toast.success(`Purchased "${nft.name}" for $${Number(result.price_paid).toFixed(2)}!`);
        onSuccess();
        onClose();
      } else {
        const code = result?.error_code;
        if (code === "frozen") {
          toast.error("This NFT is temporarily frozen due to high trade velocity. Try again in 1 hour.");
        } else if (code === "linked_account") {
          toast.error("Trade blocked: linked accounts cannot trade with each other. Multi-account trading is not allowed.");
        } else if (code === "price_changed") {
          toast.error(`Price changed (now $${Number(result.server_price).toFixed(2)}). Refresh and try again.`);
        } else {
          toast.error(result?.error || "Purchase failed");
        }
      }
    } catch (err: any) {
      toast.error(err.message || "Purchase failed");
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm bg-gray-900 border border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <ShoppingCart className="w-5 h-5 text-cyan-400" />
            Buy NFT
          </DialogTitle>
          <DialogDescription className="text-gray-400">Purchase this NFT with your virtual balance</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {nft.image_url && (
            <img src={nft.image_url} alt={nft.name} className="w-full h-48 object-cover rounded-lg" />
          )}
          <div className="flex justify-between items-center">
            <span className="font-bold text-white">{nft.name}</span>
            <span className="text-lg font-extrabold text-cyan-400">${Number(nft.current_price).toFixed(2)}</span>
          </div>
          <div className="text-sm space-y-1.5 bg-black/60 rounded-lg p-3 border border-white/5">
            <div className="flex justify-between">
              <span className="text-gray-500">Your Balance:</span>
              <span className={`font-bold ${canAfford ? "text-cyan-400" : "text-red-400"}`}>
                ${Number(balance).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">After Purchase:</span>
              <span className="font-semibold text-white">${(balance - nft.current_price).toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-500 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> Next Price:
              </span>
              <span className="font-bold text-yellow-400">${nextPrice.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-white/20 text-gray-300 hover:bg-white/10">Cancel</Button>
          <Button onClick={handlePurchase} disabled={!canAfford || purchasing} className="font-bold bg-purple-500 text-black hover:bg-purple-500 border-0">
            {purchasing ? "Purchasing..." : canAfford ? "Confirm Purchase" : "Insufficient Balance"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
